import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import MapView from "@/components/MapView";
import DriverMessages from "@/components/DriverMessages";
import DriverPTT from "@/components/DriverPTT";
import MapboxNav from "@/components/MapboxNav";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, PlayCircle, UserCheck, UserX, AlertTriangle, MapPin, Clock,
  Navigation, Flag, Loader2, Map as MapIcon, RefreshCw, School, Warehouse,
  WifiOff, CloudUpload, ArrowRightCircle, DoorOpen,
} from "lucide-react";
import { toast } from "sonner";
import { saveRoutePack, loadRoutePack, postOrQueue, queueSize, isOnline, startAutoFlush } from "@/lib/offline";

const PROBLEMS = [
  { key: "adresse_incorrecte", label: "Adresse incorrecte" },
  { key: "danger", label: "Danger" },
  { key: "autre", label: "Autre problème" },
];

const R = 6371000;
const distM = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return Infinity;
  const p = Math.PI / 180;
  const dLat = (b.lat - a.lat) * p, dLng = (b.lng - a.lng) * p;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * p) * Math.cos(b.lat * p) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const fmtDist = (m) => (m == null || !isFinite(m) ? "—" : m < 950 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

export default function DriverRoute() {
  const { assignmentId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problemOpen, setProblemOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(queueSize());
  const gpsRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/driver/routes/${assignmentId}`);
      setData(r.data);
      saveRoutePack(assignmentId, { detail: r.data });
      return r.data;
    } catch (e) {
      const pack = loadRoutePack(assignmentId);
      if (pack?.detail) {
        setData(pack.detail);
        toast.message("Mode hors ligne — données enregistrées sur l'appareil");
        return pack.detail;
      }
      toast.error("Chargement impossible");
      return null;
    }
  };
  useEffect(() => { load(); }, [assignmentId]); // eslint-disable-line

  const started = data?.assignment?.status === "in_progress";
  const stops = data?.stops || [];
  const nextIdx = stops.findIndex((s) => s.progress_status === "PENDING");
  const next = nextIdx >= 0 ? stops[nextIdx] : null;
  const done = stops.filter((s) => s.progress_status !== "PENDING" && s.progress_status !== "APPROACHING").length;
  const dropOff = data?.period === "PM";   // trajet du soir: départ de l'école = débarquement

  useEffect(() => {
    const onNet = () => setOnline(isOnline());
    window.addEventListener("online", onNet);
    window.addEventListener("offline", onNet);
    const cancel = startAutoFlush((sent) => {
      setPending(queueSize());
      toast.success(`${sent} action(s) synchronisée(s)`);
      load();
    });
    const t = setInterval(() => setPending(queueSize()), 10000);
    return () => {
      window.removeEventListener("online", onNet);
      window.removeEventListener("offline", onNet);
      clearInterval(t); cancel();
    };
  }, []); // eslint-disable-line

  // ---- Navigation Mapbox intégrée (virage-par-virage fr-CA) ----
  const [navFor, setNavFor] = useState(null);   // {stopId, label, full}

  useEffect(() => {
    if (!("geolocation" in navigator) || gpsRef.current) return;
    gpsRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const cur = { lat: p.coords.latitude, lng: p.coords.longitude, heading: p.coords.heading || 0, speed: p.coords.speed || 0 };
        setPos(cur);
        if (started) {
          postOrQueue("/driver/gps", { assignment_id: assignmentId, lat: cur.lat, lng: cur.lng, speed: cur.speed, heading: cur.heading }, { critical: false }).catch(() => {});
        }
      },
      () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
    return () => { if (gpsRef.current) { navigator.geolocation.clearWatch(gpsRef.current); gpsRef.current = null; } };
  }, [started, assignmentId]);

  const [releaseOpen, setReleaseOpen] = useState(false);
  const releaseRoute = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/routes/${assignmentId}/release`);
      toast.success("Route quittée — elle est de nouveau disponible");
      nav("/driver");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); setReleaseOpen(false); }
  };

  // ---- Actions ----
  const startRoute = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/routes/${assignmentId}/start`);
      const d = await load();
      toast.success("Route démarrée");
      if (d) saveRoutePack(assignmentId, { detail: d });
    } catch (e) {
      const msg = apiError(e.response?.data?.detail) || "Démarrage impossible";
      toast.error(msg);
      if (e.response?.status === 403) nav(`/driver/inspection?back=${assignmentId}`);
    } finally { setBusy(false); }
  };
  const completeRoute = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/routes/${assignmentId}/complete`);
      const { data: nx } = await api.get(`/driver/next`, { params: { after: assignmentId } });
      if (nx.next) {
        toast.success(`Route terminée — route suivante ${nx.next.route_number}`);
        nav(`/driver/${nx.next.id}`);
      } else {
        toast.success("Bloc terminé — retour au garage");
        nav("/driver");
      }
    } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    if (!next) return;
    setBusy(true);
    const stopId = next.id;
    try {
      const res = await postOrQueue(`/driver/routes/${assignmentId}/stop/${stopId}/status`, { status });
      // progression locale immédiate (fonctionne aussi hors réseau)
      const local = { ...data, stops: data.stops.map((s) => s.id === stopId ? { ...s, progress_status: status } : s) };
      setData(local); saveRoutePack(assignmentId, { ...(loadRoutePack(assignmentId) || {}), detail: local });
      setPending(queueSize());
      if (res?.data?.queued) toast.message("Hors réseau — action enregistrée, envoi automatique plus tard");
      if (status === "PICKED_UP" || status === "ABSENT") {
        const ns = local.stops.find((s) => s.progress_status === "PENDING");
        if (!ns) toast.success("Tous les arrêts sont faits");
      }
      if (isOnline()) load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const chooseVariant = async (variant) => {
    if (variant === data.variant) return;
    setBusy(true);
    try {
      await api.post(`/driver/routes/${assignmentId}/variant`, { variant });
      const d = await api.get(`/driver/routes/${assignmentId}`); setData(d.data);
      toast.success(variant === "original" ? "Parcours original (tel qu'importé)" : "Parcours optimisé Innova");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const reportProblem = async (type) => {
    await api.post(`/driver/routes/${assignmentId}/problem`, { problem_type: type, message: "", stop_id: next?.id });
    toast.success("Signalement envoyé au dispatch"); setProblemOpen(false);
  };
  const recalculate = async (skipStop) => {
    if (!pos) return toast.error("Position GPS indisponible");
    if (!isOnline()) return toast.error("Recalcul indisponible hors réseau");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/driver/routes/${assignmentId}/recalculate`, {
        lat: pos.lat, lng: pos.lng, blocked_stop_id: skipStop ? next?.id : null,
        reason: skipStop ? "Route bloquée à l'arrêt suivant" : "Route bloquée",
      });
      const d = await api.get(`/driver/routes/${assignmentId}`); setData(d.data);
      setProblemOpen(false);
      toast.success(`Route recalculée — sécurité ${res.metrics.safety_score}/100, dispatch avisé`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // Mapbox GL JS n'accepte qu'un jeton public (pk.) : sinon on va direct dans Google Maps.
  const mapboxOk = (process.env.REACT_APP_MAPBOX_TOKEN || "").startsWith("pk.");

  const gmapsUrl = (dest, waypoints = []) => {
    const way = waypoints.slice(0, 9).map((s) => `${s.lat},${s.lng}`).join("|");
    return "https://www.google.com/maps/dir/?api=1"
      + (pos ? `&origin=${pos.lat},${pos.lng}` : "")
      + `&destination=${dest.lat},${dest.lng}`
      + (way ? `&waypoints=${encodeURIComponent(way)}` : "")
      + "&travelmode=driving&dir_action=navigate";
  };

  /** Ouvre Google Maps (application sur Android, onglet sur ordinateur). */
  const openGmaps = (dest, waypoints = []) => {
    if (!dest?.lat) return toast.error("Coordonnées indisponibles pour cette destination");
    window.open(gmapsUrl(dest, waypoints), "_blank", "noopener");
  };

  const openGmapsFull = () => {
    const pend = stops.filter((s) => s.progress_status === "PENDING" && s.lat);
    if (!pend.length) return toast.error("Aucun arrêt à naviguer");
    openGmaps(pend[pend.length - 1], pend.slice(0, -1));
  };

  if (!data) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="animate-spin text-white/50" /></div>;

  return (
    <div className="driver-app min-h-screen bg-slate-950 text-white flex flex-col">
      <div className="px-3 py-2 flex items-center justify-between border-b border-white/10 shrink-0">
        <button className="driver-tap flex items-center justify-center text-white/80" onClick={() => nav("/driver")} data-testid="driver-back"><ArrowLeft className="w-7 h-7" /></button>
        <div className="text-center leading-tight">
          <div className="font-display text-xl font-extrabold">Route {data.route_number}</div>
          <div className="text-[11px] text-white/50">
            {data.assignment?.block_order ? `${data.period === "PM" ? "Soir" : "Matin"} · route ${data.assignment.block_order}/${data.assignment.block_total} · ` : ""}
            Autobus {data.bus_fleet} · {done}/{stops.length} arrêts
          </div>
        </div>
        <button onClick={openGmapsFull} data-testid="btn-nav-full" className="driver-tap rounded-full bg-white/5 flex items-center justify-center">
          <MapIcon className="w-6 h-6 text-white/70" />
        </button>
      </div>

      {(!online || pending > 0) && (
        <div className="mx-2 mt-2 rounded-xl bg-amber-500 text-slate-900 px-3 py-2 text-sm font-bold flex items-center gap-2 shrink-0" data-testid="offline-banner">
          {online ? <CloudUpload className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
          {online ? `Synchronisation — ${pending} action(s) en attente` : `Hors ligne${pending ? ` — ${pending} action(s) en attente` : " — guidage enregistré sur l'appareil"}`}
        </div>
      )}

      {data.variants?.original && data.variants?.optimized && (
        <div className={`mx-2 mt-2 grid grid-cols-2 gap-2 shrink-0 ${started ? "" : ""}`} data-testid="variant-picker">
          {[["original", "Original"], ["optimized", "Optimisé Innova"]].map(([key, label]) => {
            const v = data.variants[key]; const active = (data.variant || "original") === key;
            return (
              <button key={key} onClick={() => chooseVariant(key)} disabled={busy || !v} data-testid={`variant-${key}`}
                className={`driver-tap rounded-xl px-3 py-1.5 text-left border transition-colors ${active ? "bg-yellow-400 text-slate-900 border-yellow-400" : "bg-slate-900 text-white/80 border-white/15"}`}>
                <div className="text-[11px] font-bold uppercase tracking-wide">{label}</div>
                <div className="text-sm font-mono font-bold">
                  {v ? `${v.distance_km ?? "?"} km · ${Math.round(v.duration_min || 0)} min` : "—"}
                </div>
                {!started && (
                  <div className={`text-[10px] ${active ? "text-slate-700" : "text-white/60"}`}>
                    {v ? `${v.stops} arrêts · ${v.unsafe_crossings ? `${v.unsafe_crossings} traversée(s)` : "0 traversée"}` : ""}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className={`${started ? "h-[24vh]" : "h-[34vh]"} relative mt-2 shrink-0`}>
        <MapView stops={stops} geometry={[]} garage={data.garage}
          school={data.school_point} driverPosition={pos} polylineColor="#3B82F6" showNumbers
          center={pos ? [pos.lat, pos.lng] : undefined} />
        <div className="absolute top-3 left-3 right-3 bg-slate-900/80 backdrop-blur rounded-xl p-2 flex items-center gap-2 border border-white/10">
          <div className="h-2 flex-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${stops.length ? (done / stops.length) * 100 : 0}%` }} /></div>
          <span className="text-xs font-mono">{done}/{stops.length}</span>
        </div>
      </div>

      <div className="flex-1 px-3 pt-2 pb-1 flex flex-col min-h-0">
        {!started ? (
          <>
          <button onClick={startRoute} disabled={busy} data-testid="btn-start"
            className="w-full h-20 rounded-2xl bg-emerald-500 text-slate-900 font-display text-2xl font-extrabold flex items-center justify-center gap-3 active:scale-95 transition-transform">
            <PlayCircle className="w-8 h-8" /> Démarrer la route
          </button>
          <button onClick={() => setReleaseOpen(true)} disabled={busy} data-testid="btn-release"
            className="w-full h-14 mt-2 rounded-2xl bg-white/5 border border-red-500/40 text-red-300 font-display text-base font-extrabold flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <DoorOpen className="w-5 h-5" /> QUITTER CETTE ROUTE
          </button>
          </>
        ) : next ? (
          <>
            <div className="rounded-2xl bg-slate-900 border border-white/10 px-3 py-2.5 mb-2 overflow-y-auto">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">Arrêt #{nextIdx + 1}</span>
                <span className="text-sm font-mono font-bold text-yellow-400 flex items-center gap-1"><Clock className="w-4 h-4" />{next.pickup_time || "—"}</span>
              </div>
              <div className="font-display text-2xl font-extrabold leading-tight mt-0.5">{next.address}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[13px]">
                <span className="text-white/60">{next.city}</span>
                <span className="flex items-center gap-1 text-white/60"><MapPin className="w-4 h-4" />{fmtDist(distM(pos, next))}</span>
                {next.student_name && <span className="flex items-center gap-1"><UserCheck className="w-4 h-4 text-blue-400" />{next.student_name}</span>}
              </div>
              {next.road_number && (
                <div className="mt-1.5 rounded-lg bg-red-500/15 border border-red-500/40 px-2 py-1 text-[13px] font-bold text-red-300 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> Route {next.road_number} — {dropOff ? "débarquement" : "ramassage"} côté {next.required_stop_side}
                </div>
              )}
              <div className="mt-1.5 text-[11px] text-white/50 flex items-center gap-1.5" data-testid="school-target">
                {data.returns_to_garage
                  ? <><Warehouse className="w-4 h-4" /> Fin du bloc — retour au garage</>
                  : dropOff
                    ? <><School className="w-4 h-4" /> Débarquement — départ de l'école {data.school_arrival_max || ""}</>
                    : <><School className="w-4 h-4" /> École au plus tard {data.school_arrival_max || "—"}</>}
              </div>
              {next.notes && <div className="mt-1.5 text-[13px] bg-white/5 rounded-lg px-2 py-1.5">{next.notes}</div>}
            </div>

            <div className="driver-bar mt-auto space-y-2 pt-1">
              <button onClick={() => openGmaps(next)} data-testid="btn-navigate"
                className="w-full h-16 rounded-2xl bg-blue-600 text-white font-display text-xl font-extrabold flex items-center justify-center gap-2.5 active:scale-95 transition-transform">
                <Navigation className="w-7 h-7" /> NAVIGUER <span className="text-sm font-semibold opacity-80">(Google Maps)</span>
              </button>
              <div className="grid grid-cols-2 gap-2">
                <ActionBtn onClick={() => setStatus("ARRIVED")} disabled={busy} color="bg-blue-500" icon={MapPin} label="ARRIVÉ" testid="btn-arrive" />
                <ActionBtn onClick={() => setStatus("PICKED_UP")} disabled={busy} color="bg-emerald-500 text-slate-900" icon={UserCheck} label={dropOff ? "DÉBARQUÉ" : "EMBARQUÉ"} testid="btn-boarded" />
                <ActionBtn onClick={() => setStatus("ABSENT")} disabled={busy} color="bg-amber-500 text-slate-900" icon={UserX} label="ABSENT" testid="btn-absent" />
                <ActionBtn onClick={() => setProblemOpen(true)} disabled={busy} color="bg-red-500" icon={AlertTriangle} label="PROBLÈME" testid="btn-problem" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => recalculate(false)} disabled={busy} data-testid="btn-recalc"
                  className="driver-tap rounded-xl bg-orange-500 text-slate-900 font-bold text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50">
                  <RefreshCw className="w-5 h-5" /> Bloquée
                </button>
                <button onClick={openGmapsFull} data-testid="btn-nav-all"
                  className="driver-tap rounded-xl bg-white/5 border border-white/15 text-white/80 font-semibold text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <MapIcon className="w-5 h-5" /> Toute la route
                </button>
                <button onClick={() => (mapboxOk
                  ? setNavFor({ stopId: next.id, label: next.address, full: false })
                  : openGmaps(next))} data-testid="btn-nav-inapp"
                  className="driver-tap rounded-xl bg-white/5 border border-white/15 text-white/60 font-semibold text-xs flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Navigation className="w-4 h-4" /> {mapboxOk ? "Guidage intégré" : "Guidage GPS"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-2" data-testid="finish-choice">
            <div className="text-center text-sm text-white/60">Tous les arrêts sont faits — que faites-vous ?</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => openGmaps(data.garage)}
                data-testid="btn-to-garage"
                className="h-[72px] rounded-2xl bg-blue-600 text-white font-display text-base font-extrabold flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-40">
                <Warehouse className="w-6 h-6" /> RETOUR AU GARAGE
              </button>
              <button onClick={completeRoute} disabled={busy} data-testid="btn-next-trip"
                className="h-[72px] rounded-2xl bg-emerald-500 text-slate-900 font-display text-base font-extrabold flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50">
                <ArrowRightCircle className="w-6 h-6" /> PROCHAIN TRAJET
              </button>
            </div>
            <button onClick={completeRoute} disabled={busy} data-testid="btn-complete"
              className="w-full h-16 rounded-2xl bg-slate-800 border border-emerald-500 text-emerald-400 font-display text-lg font-extrabold flex items-center justify-center gap-3 active:scale-95 transition-transform">
              <Flag className="w-6 h-6" /> Terminer la route
            </button>
          </div>
        )}
      </div>

      <Dialog open={problemOpen} onOpenChange={setProblemOpen}>
        <DialogContent className="bg-slate-900 text-white border-white/10" data-testid="problem-dialog">
          <DialogHeader><DialogTitle className="font-display">Signaler un problème</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 mt-2">
            <button onClick={() => recalculate(true)} disabled={busy} data-testid="problem-route_bloquee"
              className="h-16 rounded-xl bg-orange-500 text-slate-900 font-bold text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <RefreshCw className="w-5 h-5" /> Route bloquée — recalculer
            </button>
            {PROBLEMS.map((p) => (
              <button key={p.key} onClick={() => reportProblem(p.key)} data-testid={`problem-${p.key}`}
                className="h-16 rounded-xl bg-white/5 border border-white/15 font-semibold text-lg active:scale-95 transition-transform">{p.label}</button>
            ))}
            <button onClick={() => { setProblemOpen(false); setReleaseOpen(true); }} data-testid="btn-release-started"
              className="h-16 rounded-xl bg-white/5 border border-red-500/40 text-red-300 font-semibold text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <DoorOpen className="w-5 h-5" /> Quitter cette route
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={releaseOpen} onOpenChange={setReleaseOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white" data-testid="release-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Quitter la route {data.route_number} ?</DialogTitle></DialogHeader>
          <p className="text-sm text-white/70">
            La route redeviendra disponible pour un autre chauffeur et le répartiteur sera averti.
            Vous pourrez ensuite prendre une autre route.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button onClick={() => setReleaseOpen(false)} data-testid="release-cancel"
              className="h-14 rounded-xl bg-white/5 border border-white/15 font-bold">Annuler</button>
            <button onClick={releaseRoute} disabled={busy} data-testid="release-confirm"
              className="h-14 rounded-xl bg-red-500 text-white font-display font-extrabold disabled:opacity-50">QUITTER</button>
          </div>
        </DialogContent>
      </Dialog>
      {navFor && (
        <MapboxNav assignmentId={assignmentId} stopId={navFor.stopId} full={navFor.full}
          dest={navFor.dest} label={navFor.label} onProgress={load} dropOff={dropOff}
          center={next?.lat ? [next.lng, next.lat] : (data.garage?.lat ? [data.garage.lng, data.garage.lat] : null)}
          onNextTrip={() => { setNavFor(null); completeRoute(); }}
          onUseGoogleMaps={() => (navFor.dest === "garage" ? openGmaps(data.garage)
            : navFor.full ? openGmapsFull() : openGmaps(next))}
          onClose={() => { setNavFor(null); load(); }} />
      )}
      <DriverMessages />
      <DriverPTT floating />
    </div>
  );
}

function ActionBtn({ onClick, disabled, color, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`h-[72px] rounded-2xl ${color} text-white font-display text-lg font-extrabold flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform disabled:opacity-50`}>
      <Icon className="w-6 h-6" /> {label}
    </button>
  );
}
